import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';

import { flowsApi } from 'apis/flows';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

import type { ScheduleModalProps } from '../../openingHours.view';
import { EditModal, EditModalSubmitProps } from '../editModal/editModal';

type Props =
  | ({
      mode: 'update';
    } & ScheduleModalProps)
  | {
      mode: 'edit';
      name: string;
      onClose: VoidFunction;
      onSubmit: (name: string) => void;
    };

export const RenameModal = (props: Props): JSX.Element => {
  const { mode, onClose } = props;

  const notify = useNotifications();
  const [readFlowsSchedule, { data: readFlowScheduleResponse }] =
    flowsApi.useLazyReadFlowScheduleQuery();
  const [updateSchedule, { isLoading }] =
    flowsApi.useUpdateFlowScheduleMutation();

  const isEditMode = mode === 'edit';

  useEffectOnce(() => {
    if (!isEditMode) {
      readFlowsSchedule({ id: props.schedule.id });
    }
  });

  const handleSubmit = useCallback(
    async ({ name }: EditModalSubmitProps) => {
      ampli.businessHoursRenameScheduleFormSubmitted({
        view: mode === 'edit' ? 'edit' : 'list',
      });

      if (isEditMode) {
        props.onSubmit(name);
        onClose();
        return;
      }

      const { schedule, refetchItems } = props;

      if (readFlowScheduleResponse) {
        const { announcement, auto_reply_template_id, country_code, version } =
          readFlowScheduleResponse;

        const payload = {
          auto_reply_template_id,
          announcement,
          country_code,
          name,
          timezone: schedule.timezone,
          version,
        };

        const res = await updateSchedule({
          id: schedule.id,
          schedule: payload,
        });

        if ('data' in res) {
          notify(
            {
              title: 'Schedule renamed!',
              time: 5000,
            },
            NotificationType.Success,
          );
          refetchItems?.();
          onClose();
        } else {
          notify(
            {
              title: `An error occurred when trying to rename "${name}" schedule.`,
            },
            NotificationType.Error,
          );
        }
      } else {
        notify(
          {
            title: 'An error occurred when trying to fetch current schedule.',
          },
          NotificationType.Error,
        );
      }
    },
    [
      isEditMode,
      mode,
      props,
      readFlowScheduleResponse,
      notify,
      onClose,
      updateSchedule,
    ],
  );

  return (
    <EditModal
      isLoading={isLoading}
      name={isEditMode ? props.name : props.schedule.name}
      onClose={() => {
        onClose();
        ampli.businessHoursRenameScheduleFormCancelled();
      }}
      onSubmit={handleSubmit}
      title="Rename schedule"
    />
  );
};
